/**
 * MM-1123: shape of one sibling (child profile) as returned by the GET_SIBLING API, limited to the
 * fields the sibling-switching UI reads.
 */
export interface SiblingProfile {
  /** Patient id of this child; becomes user.patientId when the guardian switches to this child. */
  id: number;
  /** Child's first name, stored lower case by some flows, so always display it via formatSiblingName. */
  firstName: string;
  /** Child's last name, see firstName. */
  lastName: string;
  /** CometChat user id of this child; becomes user.secureMessageUserId on switch. */
  cometUserId?: string;
}

/**
 * Accent colours given to children by their position in the sibling list, so each child keeps the
 * same colour wherever they appear (avatar, greeting, banner). Dark blue text stays readable on all.
 */
const SIBLING_COLORS = ['#FFC44D', '#A0B2FA', '#9F84FF', '#7FD4C1', '#FFB4A2', '#C5E384'];

/**
 * Capitalises the first letter of a name part, matching how the header already displays siblings.
 */
const capitalise = (value?: string): string => (value ? value.charAt(0).toUpperCase() + value.slice(1) : '');

/**
 * Returns the child's display name, e.g. "Ava Thompson".
 */
export const formatSiblingName = (sibling?: SiblingProfile): string =>
  sibling ? `${capitalise(sibling.firstName)} ${capitalise(sibling.lastName)}`.trim() : '';

/**
 * Returns the child's capitalised first name, used in the dashboard greeting.
 */
export const formatSiblingFirstName = (sibling?: SiblingProfile): string => capitalise(sibling?.firstName);

/**
 * Returns the child's two-letter initials for avatars, e.g. "AT".
 */
export const siblingInitials = (sibling?: SiblingProfile): string =>
  sibling ? `${sibling.firstName?.charAt(0) ?? ''}${sibling.lastName?.charAt(0) ?? ''}`.toUpperCase() : '';

/**
 * Returns the accent colour of the child at the given position in the sibling list.
 */
export const siblingColor = (siblings: SiblingProfile[] | undefined, sibling?: SiblingProfile): string => {
  const index = sibling && siblings ? siblings.findIndex((s) => s.id === sibling.id) : -1;
  return SIBLING_COLORS[(index < 0 ? 0 : index) % SIBLING_COLORS.length];
};

/**
 * Returns the sibling whose profile is currently open (matched on user.patientId).
 */
export const findActiveSibling = (
  siblings: SiblingProfile[] | undefined,
  patientId: number | undefined
): SiblingProfile | undefined => siblings?.find((s) => s.id === patientId);
